<template>
  <teleport to="body">
    <section class="popup" v-if="show">
      <div v-if="success" class="form_success" ref="formSuccessRef">
        <p class="success">{{ t("booking.request_sent") }}</p>
        <div class="close-popup" @click="setCallBack(false)"></div>
      </div>
      <div v-else class="form" ref="formRef">
        <div class="content">
          <div class="logo">
            <img alt="Leon Hotel Logo" src="/img/logo_small.svg" />
          </div>
          <div class="h">{{ t("booking.plan_arrival") }}</div>
          <form @submit.prevent="submitOrder">
            <div
              class="mdl-textfield mdl-js-textfield mb mdl-textfield--floating-label txt-full-width is-upgraded is-dirty"
            >
              <input 
                class="mdl-textfield__input" 
                type="tel" 
                :value="formState.phone" 
                @input="handlePhoneInput"
                :class="{ 'error': showPhoneError }"
              />
              <label class="mdl-textfield__label" :class="{ 'position_label': formState.phone }">
                {{ t("booking.phone") }}
              </label>
              <span v-if="showPhoneError" class="error-text">{{ t("booking.invalid_phone") }}</span>
            </div>

            <div
              class="mdl-textfield mdl-js-textfield mdl-textfield--floating-label txt-full-width is-upgraded is-dirty mb"
            >
              <input 
                class="mdl-textfield__input" 
                type="text" 
                :value="formState.name" 
                @input="handleNameInput"
              />
              <label class="mdl-textfield__label" :class="{ 'position_label': formState.name }">
                {{ t("booking.name") }}
              </label>
            </div>

            <div class="mdl-textfield">
              <button 
                type="submit" 
                class="mdl-textfield__button"
                :disabled="!isFormValid || isSubmitting"
                :class="{ 'submitting': isSubmitting }"
              >
                {{ t("booking.request_call") }}
              </button>
            </div>
          </form>
        </div>
        <div class="borders b-d-left b-d-right b-d-top b-d-bottom"></div>
        <div class="close-popup" @click="setCallBack(false)"></div>
      </div>
    </section>
  </teleport>
</template>

<script setup lang="ts">
import { onClickOutside } from '@vueuse/core'

interface FormState {
  name: string
  phone: string
  checkIn: Date | null
  checkOut: Date | null
  email: string
  room: string
}

const formRef = ref<HTMLDivElement | null>(null)
const formSuccessRef = ref<HTMLDivElement | null>(null)
const { t } = useI18n()
const mainStore = useMainStore()

// Form State
const formState = reactive<FormState>({
  name: '',
  phone: '',
  checkIn: null,
  checkOut: null,
  email: '',
  room: ''
})

const showPhoneError = ref(false)
const isPhoneValid = ref(false)
const isSubmitting = ref(false)
const success = ref(false)

// Computed
const show = computed(() => mainStore.callBack)
const isFormValid = computed(() => isPhoneValid.value && formState.name.length > 0)

// Click Outside Handlers
onClickOutside(formRef, () => closePopup())
onClickOutside(formSuccessRef, () => closePopup())

// Methods
const validPhone = (phone: string): boolean => {
  const valid = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im
  return valid.test(phone)
}

const handlePhoneInput = (event: Event) => {
  const value = (event.target as HTMLInputElement).value
  formState.phone = value
  isPhoneValid.value = validPhone(value)
  showPhoneError.value = value.length > 0 && !isPhoneValid.value
}

const handleNameInput = (event: Event) => {
  formState.name = (event.target as HTMLInputElement).value
}

const setCallBack = (value: boolean) => {
  mainStore.setCallBack(value)
}

const resetForm = () => {
  formState.name = ''
  formState.phone = ''
  formState.checkIn = null
  formState.checkOut = null
  formState.email = ''
  formState.room = ''
  isPhoneValid.value = false
  showPhoneError.value = false
}

const closePopup = () => {
  if (show.value) {
    mainStore.setCallBack(false)
    success.value = false
    resetForm()
  }
}

const submitOrder = async () => {
  if (!isFormValid.value) {
    showPhoneError.value = !isPhoneValid.value
    return
  }

  try {
    isSubmitting.value = true
    const response = await $fetch('https://api.leonhotel.ru/api/v1/orders', {
      method: 'POST',
      body: formState
    })

    if (response) {
      success.value = true
      resetForm()
    }
  } catch (error) {
    console.error('Error submitting order:', error)
  } finally {
    isSubmitting.value = false
  }
}

// Cleanup
onBeforeUnmount(() => {
  success.value = false
  resetForm()
})
</script>

<style lang="scss" scoped>
@import "../assets/scss/_variables.scss";

.popup {
  position: fixed !important;
  display: flex;
  background-color: rgba(0, 0, 0, 0.58);
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 100500;
}

.popup .form {
  width: 1170px;
  height: 450px;
  background-color: white;
  margin: auto;
  position: relative;
  display: flex;
  padding-left: 57px;
  padding-right: 57px;
  padding-top: 57px;
  padding-bottom: 57px;
  z-index: 4;
}

.popup .form_success {
  width: 1170px;
  height: 450px;
  background-color: white;
  margin: auto;
  position: relative;
  padding-left: 57px;
  padding-right: 57px;
  padding-top: 57px;
  padding-bottom: 57px;
  z-index: 4;
}

.success {
  font-family: "Lato";
  font-size: 35px;
  text-align: center;
  margin-top: 12rem;
  line-height: 24px;
}

.popup .form .borders {
  position: absolute;
  left: 55px;
  right: 55px;
  top: 55px;
  bottom: 55px;
}

.popup .form .content {
  position: relative;
  width: 100%;
  max-width: 100%;
  flex: (0, 0, 100%);
}

.popup .form .content .h {
  // text-transform: uppercase;
  font-family: "Lato";
  font-size: 22px;
  text-align: center;
  line-height: 24px;
  margin-top: 66px;
  margin-bottom: 4px;
  margin-left: auto;
  margin-right: auto;
  position: relative;
  top: -12px;
}

.popup .form .content form {
  min-width: 300px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  display: grid;
}

.mb {
  margin-bottom: 12px;
}

.popup .form .content form div {
  width: auto;
  // flex: (0 0 auto);
}

.mdl-textfield {
  padding: 0px;
  padding-top: 20px;
  margin-top: 15px;
  z-index: 5;
}

.popup .form .content form .mdl-textfield .mdl-textfield__input {
  border-bottom: 1px solid #707070 !important;
  // margin-top: 0px !important;
  // height: 55px;
  // padding-left: 11px;
  font-family: "Lato";
  font-size: 16px;
  // color: black;
  outline: none;
}

.position_label {
  margin-top: -33px;
}

form input {
  padding-bottom: 1rem;
}

input:focus~label,
label:active {
  margin-top: -33px;
  transition: all 0.3s ease-in-out;
}

.popup .form .content form .mdl-textfield .mdl-textfield__label {
  color: #8d8d8d;
  font-family: "Lato" !important;
  font-size: 16px !important;
  top: 20px;
  transition: all 0.3s ease-in-out;
}

.popup .form .content form .mdl-textfield .mdl-textfield__button {
  color: black !important;
  font-family: "Lato" !important;
  font-size: 16px !important;
  height: 50px;
  width: 236px;
  margin-top: 0;
  background-color: #d9ccaf;
  display: block;
  margin-left: auto;
  border: none !important;
  font-family: "Lato";
  font-size: 16px;
}

.close-popup {
  position: absolute;
  right: 21px;
  top: 19px;
  background-image: url(/img/close-popup.svg);
  background-size: cover;
  cursor: pointer;
  height: 51px;
  width: 51px;
}

.popup .logo {
  position: absolute;
  left: -32px;
  top: -20px;
  height: 69px;
  width: 120px;
  background-color: white;
  padding-left: 16px;
  padding-right: 16px;
}

.popup .logo img {
  height: 100%;
  filter: invert(1);
}

@media screen and (max-width: 768px) {
  .popup .form .content .h {
    margin-top: 90px;
  }

  .popup .form {
    width: 1170px;
    height: 500px;
  }

  .close-popup {
    top: 40px;
  }
}

@media screen and (max-width: 600px) {
  .popup .form .content form .mdl-textfield .mdl-textfield__button {
    margin-right: auto;
  }
}

@media screen and (max-width: 600px) {
  .form {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    height: 100% !important;
  }

  .popup .form .content .h {
    margin-top: 12rem;
  }
}

@media screen and (max-width: 425px) {
  .popup .form {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }

  .popup .logo {
    left: 0 !important;
    right: 0 !important;
    margin: auto !important;
    top: 2px !important;
  }

  .success {
    margin-top: 14rem;
    font-size: 30px;
    line-height: 30px;
  }

  .popup .form .content .h {
    padding: 0 25px;
  }

  .popup .form .content form {
    width: 310px !important;
  }

  .close-popup {
    right: 20px;
    top: 30px;
  }
}

@media screen and (max-width: 330px) {
  .popup .form .content form {
    padding: 0 20px;
  }

  .popup .form .content .h {
    padding: 0 20px;
  }

  .close-popup {
    right: 5px;
  }
}

.error {
  border-color: red !important;
}

.error-text {
  color: red;
  font-size: 12px;
  margin-top: 4px;
  font-family: "Lato";
}

.mdl-textfield__button {
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.submitting {
    opacity: 0.7;
    cursor: wait;
  }
}
</style>
